import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

export default function StarterKit() {
  return (
    <div className="min-h-screen bg-[#111111] text-white">

      <Helmet>
        <title>Ajax Baseline Security System | SeguraTech</title>

        <meta
          name="description"
          content="Discover the Ajax Baseline Security System from SeguraTech. A complete professional wireless alarm system built around the Ajax Hub 2, designed to grow with your property."
        />
      </Helmet>

      {/* Breadcrumb */}

      <section className="max-w-6xl mx-auto px-4 pt-10 text-sm text-white/60">

        <Link to="/alarms/ajax" className="hover:text-white">
          Ajax
        </Link>

        {" → "}

        <Link
          to="/alarms/ajax/intrusion-detection"
          className="hover:text-white"
        >
          Security Systems
        </Link>

        {" → "}

        Ajax Baseline Security System

      </section>

      {/* Hero */}

      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-12 items-center">

        <div>

          <h1 className="text-4xl font-bold text-[#C6A564] mb-6">
            Ajax Baseline Security System
          </h1>

          <p className="text-white/80 mb-6 leading-8">

            The Ajax Baseline Security System is a complete professional
            wireless alarm system built using devices from Ajax's Baseline
            product range. Designed to protect homes, apartments and small
            business premises, it combines intelligent intrusion detection,
            reliable communication and intuitive control through the Ajax app.

          </p>

          <p className="text-white/80 mb-6 leading-8">

            Every system is built around the Ajax Hub 2 and provides a reliable
            foundation that can be expanded as your security requirements
            evolve. Whether you're protecting a single entrance or securing an
            entire property, additional detectors, sirens, cameras, automation
            devices and life safety products can all be added without replacing
            the equipment you've already invested in.

          </p>

          <div className="border border-[#C6A564] rounded-xl p-6 bg-[#1A1A1A]">

            <p className="text-2xl font-semibold text-[#C6A564] mb-2">
              Systems from £259.99
            </p>

            <p className="text-white/80">
              Professional installation from £199.99
            </p>

          </div>

        </div>

        <div className="flex justify-center">

          <img
            src={`${process.env.PUBLIC_URL}/AjaxKit/StarterKit/01.png`}
            alt="Ajax Baseline Security System including Hub 2, MotionProtect, DoorProtect, HomeSiren and SpaceControl"
            className="max-h-[420px] object-contain"
          />

        </div>

      </section>

      {/* Core Components */}

      <section className="max-w-6xl mx-auto px-4 py-16 border-t border-[#2E2E2E]">

        <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
          Core Components
        </h2>

        <p className="text-white/80 max-w-4xl leading-8 mb-12">

          Every Ajax Baseline Security System includes the essential devices
          needed to protect your property from day one. Each component has been
          designed to work seamlessly with the others, creating a single
          intelligent security system that can continue to grow as your
          requirements change.

        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8">

          {/* Hub 2 */}

          <div className="text-center">

            <img
              src={`${process.env.PUBLIC_URL}/AjaxKit/StarterKit/02.png`}
              alt="Ajax Hub 2 control panel"
              className="h-32 mx-auto mb-4 object-contain"
            />

            <h3 className="font-medium mb-3 text-[#C6A564]">
              Ajax Hub 2
            </h3>

            <p className="text-sm text-white/70 leading-7">

              The Hub 2 is the centre of the entire security system. Every
              detector, siren and remote device communicates with the hub,
              allowing it to monitor your property continuously and respond
              immediately whenever an event occurs. Supporting multiple
              communication methods and up to 100 Ajax devices, it provides a
              reliable platform that can grow alongside your security
              requirements.

            </p>

          </div>

          {/* SpaceControl */}

          <div className="text-center">

            <img
              src={`${process.env.PUBLIC_URL}/AjaxKit/StarterKit/03.png`}
              alt="Ajax SpaceControl remote control"
              className="h-32 mx-auto mb-4 object-contain"
            />

            <h3 className="font-medium mb-3 text-[#C6A564]">
              SpaceControl
            </h3>

            <p className="text-sm text-white/70 leading-7">

              SpaceControl offers a quick and convenient way to arm or disarm
              your security system without opening the mobile app. Small enough
              to fit comfortably on your keyring, it also features a panic
              button that can send an emergency alert when required, making it
              ideal for everyday use.

            </p>

          </div>

          {/* DoorProtect */}

          <div className="text-center">

            <img
              src={`${process.env.PUBLIC_URL}/AjaxKit/StarterKit/04.png`}
              alt="Ajax DoorProtect sensor"
              className="h-32 mx-auto mb-4 object-contain"
            />

            <h3 className="font-medium mb-3 text-[#C6A564]">
              DoorProtect
            </h3>

            <p className="text-sm text-white/70 leading-7">

              DoorProtect monitors the opening of doors and windows using a
              reliable magnetic contact. Compact and discreet, it immediately
              reports activity to the Hub 2 whenever a protected opening is
              used. It can also be connected to compatible third-party wired
              devices, providing additional flexibility where required.

            </p>

          </div>

          {/* MotionProtect */}

          <div className="text-center">

            <img
              src={`${process.env.PUBLIC_URL}/AjaxKit/StarterKit/05.png`}
              alt="Ajax MotionProtect detector"
              className="h-32 mx-auto mb-4 object-contain"
            />

            <h3 className="font-medium mb-3 text-[#C6A564]">
              MotionProtect
            </h3>

            <p className="text-sm text-white/70 leading-7">

              MotionProtect provides reliable indoor motion detection using
              advanced sensing technology designed to distinguish between
              genuine movement and common causes of false alarms. Positioned in
              key areas of the property, it forms one of the primary layers of
              protection within the system.

            </p>

          </div>

          {/* HomeSiren */}

          <div className="text-center">

            <img
              src={`${process.env.PUBLIC_URL}/AjaxKit/StarterKit/06.png`}
              alt="Ajax HomeSiren"
              className="h-32 mx-auto mb-4 object-contain"
            />

            <h3 className="font-medium mb-3 text-[#C6A564]">
              HomeSiren
            </h3>

            <p className="text-sm text-white/70 leading-7">

              HomeSiren provides an immediate audible warning whenever the alarm
              is activated. Its adjustable volume allows it to suit a wide
              variety of properties, while optional entry, exit and door chime
              notifications make it useful as part of your everyday routine as
              well as during alarm events.

            </p>

          </div>

        </div>

      </section>

      {/* Designed to Grow */}

      <section className="max-w-6xl mx-auto px-4 py-16 border-t border-[#2E2E2E]">

        <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
          Designed to Grow With You
        </h2>

        <p className="text-white/80 leading-8 mb-8 max-w-4xl">

          One of the greatest strengths of the Ajax Baseline Security System is
          its flexibility. The system supplied today doesn't have to remain the
          same forever. As your property or your requirements change, new
          devices can be added without replacing the equipment you've already
          invested in.

        </p>

        <p className="text-white/80 leading-8 mb-10 max-w-4xl">

          Whether you're extending your home, converting a garage into a home
          office, securing an outbuilding or simply adding extra protection,
          every new device integrates seamlessly into the same Ajax ecosystem
          and is managed through the same easy-to-use mobile app.

        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">

          <div className="bg-[#1A1A1A] border border-[#2E2E2E] rounded-xl p-5">
            Additional Motion Detectors
          </div>

          <div className="bg-[#1A1A1A] border border-[#2E2E2E] rounded-xl p-5">
            Door & Window Protection
          </div>

          <div className="bg-[#1A1A1A] border border-[#2E2E2E] rounded-xl p-5">
            Outdoor Detection
          </div>

          <div className="bg-[#1A1A1A] border border-[#2E2E2E] rounded-xl p-5">
            Indoor & Outdoor Sirens
          </div>

          <div className="bg-[#1A1A1A] border border-[#2E2E2E] rounded-xl p-5">
            Fire & Carbon Monoxide Detection
          </div>

          <div className="bg-[#1A1A1A] border border-[#2E2E2E] rounded-xl p-5">
            Water Leak Detection
          </div>

          <div className="bg-[#1A1A1A] border border-[#2E2E2E] rounded-xl p-5">
            Video Doorbells
          </div>

          <div className="bg-[#1A1A1A] border border-[#2E2E2E] rounded-xl p-5">
            Cameras & Photo Verification
          </div>

          <div className="bg-[#1A1A1A] border border-[#2E2E2E] rounded-xl p-5">
            Smart Automation Devices
          </div>

        </div>

      </section>
      {/* How the System Works */}

      <section className="max-w-6xl mx-auto px-4 py-16 border-t border-[#2E2E2E]">

        <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
          How the System Works
        </h2>

        <div className="max-w-4xl space-y-6 text-white/80 leading-8 mb-12">

          <p>

            At the heart of every Ajax security system is the Hub 2. Think of
            it as the central control unit that keeps every part of the system
            working together. Every detector, siren and remote device
            communicates with the hub, allowing it to monitor the status of
            your entire system around the clock.

          </p>

          <p>

            When a detector identifies an event, whether that's a door opening,
            movement inside the property or another security event, the
            information is sent to the Hub 2 almost instantly. The hub
            processes the event and immediately notifies you through the Ajax
            app. If your system is connected to professional monitoring, the
            same event can also be forwarded to the monitoring centre for the
            appropriate response.

          </p>

          <p>

            Every device communicates using Ajax's secure wireless technology,
            designed specifically for security applications. Rather than
            operating as a collection of individual devices, every component
            works together as part of one intelligent ecosystem, providing fast
            communication, continuous supervision and reliable operation.

          </p>

        </div>

        <img
          src={`${process.env.PUBLIC_URL}/AjaxKit/StarterKit/07.png`}
          alt="Illustration showing how Ajax devices communicate with the Hub 2"
          className="mx-auto max-h-[500px] object-contain"
        />

      </section>

      {/* Complete Control */}

      <section className="max-w-6xl mx-auto px-4 py-16 border-t border-[#2E2E2E]">

        <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
          Complete Control Through the Ajax App
        </h2>

        <div className="max-w-4xl space-y-6 text-white/80 leading-8 mb-12">

          <p>

            Every Ajax security system is managed through the award-winning
            Ajax app, giving you complete control whether you're at home, at
            work or away from your property.

          </p>

          <p>

            The app has been designed to make everyday operation simple. With
            just a few taps you can arm or disarm your system, check the status
            of every connected device, receive instant notifications and review
            your event history.

          </p>

          <p>

            As your system expands, the app grows with it. Additional detectors,
            cameras, video doorbells, automation devices and new users can all
            be managed from the same intuitive interface, giving you one place
            to control your entire security system.

          </p>

        </div>

        <img
          src={`${process.env.PUBLIC_URL}/AjaxKit/StarterKit/08.png`}
          alt="Ajax mobile application showing security system controls"
          className="mx-auto max-h-[500px] object-contain"
        />

      </section>
            {/* Why Ajax? */}

      <section className="max-w-6xl mx-auto px-4 py-16 border-t border-[#2E2E2E]">

        <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
          Built Around the Ajax Ecosystem
        </h2>

        <div className="max-w-4xl space-y-6 text-white/80 leading-8">

          <p>

            Every device in the Ajax Baseline Security System has been designed
            to work as part of one complete ecosystem. Rather than combining
            products from multiple manufacturers, Ajax develops its hardware,
            software and mobile applications together, ensuring every component
            works seamlessly with the others.

          </p>

          <p>

            As your security requirements change, your system can evolve with
            them. Additional detectors, sirens, cameras, life safety devices,
            automation products and accessories can all be added without
            replacing your existing equipment, giving you the flexibility to
            expand your system at your own pace.

          </p>

          <p>

            Regular software updates continue to introduce new features and
            improvements, helping your system remain current long after it has
            been installed. This commitment to continual development is one of
            the reasons Ajax has become one of the world's fastest-growing
            professional security systems.

          </p>

        </div>

      </section>

      {/* Call To Action */}

      <section className="max-w-5xl mx-auto px-4 py-20">

        <div className="border border-[#C6A564] rounded-2xl bg-[#1A1A1A] p-12 text-center">

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            Ready to Build Your Ajax Security System?
          </h2>

          <p className="text-white/80 leading-8 max-w-3xl mx-auto mb-10">

            Whether you're securing your first property or upgrading an
            existing alarm system, we'll help you design an Ajax security
            system that's tailored to your requirements today while remaining
            ready for tomorrow.

          </p>

          <Link
            to="/contact"
            className="inline-block bg-[#C6A564] text-[#111111] font-semibold px-10 py-4 rounded-xl hover:opacity-90 transition"
          >
            Contact SeguraTech
          </Link>

        </div>

      </section>

    </div>
  );
}
